import { Action } from "redux";
import { ThunkAction } from "redux-thunk";
import queryString from "query-string";
import { endpoints } from "../Constants/endpoints";
import { IAppState, ApiRequest } from "../../Common";
import ResponseHandler from "../../App/ResponseHandler";
import { ResponseResult } from "../../Common/Models/ResponseResult";
import { UserAutocompleteItem } from "../../Layout/Models/AutocompleteItem";

interface IUserAutocompleteSearchAction extends Action {
  type: UserAutocompleteSearchActionType;
  payload: any;
}

// ARGUMENT TYPES

export interface IGetUserAutocompleteResultsArguments {
  searchQuery: string;
  maxNumToGet?: number;
}

// Payload Types

export interface IUserAutocompleteSearchLoadedPayload {
  loadedItems: UserAutocompleteItem[];
}

export interface ISetUserAutocompleteSearchErroredPayload {
  errorCode: number;
  errorDescription: string;
}

export interface IUserAutocompleteSearchUnloadPayload {}

export interface ISetUserAutocompleteSearchLoadingPayload {}

// ACTION TYPES

export interface IUserAutocompleteSearchLoadedAction extends IUserAutocompleteSearchAction {
  payload: IUserAutocompleteSearchLoadedPayload;
}

// Action for the FEEDBACK_SEARCH_SET_ERRORED action
export interface ISetUserAutocompleteSearchErroredAction extends IUserAutocompleteSearchAction {
  payload: ISetUserAutocompleteSearchErroredPayload;
}

export interface IUserAutocompleteSearchUnloadAction extends IUserAutocompleteSearchAction {
  payload: IUserAutocompleteSearchUnloadPayload;
}

export interface ISetUserAutocompleteSearchLoadingAction extends IUserAutocompleteSearchAction {
  payload: ISetUserAutocompleteSearchLoadingPayload;
}

// union of all action types. If you define a new action type, add it here por favor
export type UserAutocompleteSearchAction =
  | IUserAutocompleteSearchLoadedAction
  | ISetUserAutocompleteSearchErroredAction
  | IUserAutocompleteSearchUnloadAction
  | ISetUserAutocompleteSearchLoadingAction;

export enum UserAutocompleteSearchActionType {
  USER_AUTOCOMPLETE_SEARCH_LOADED = "USER_AUTOCOMPLETE_SEARCH_LOADED",
  USER_AUTOCOMPLETE_SEARCH_UNLOAD = "USER_AUTOCOMPLETE_SEARCH_UNLOAD",
  USER_AUTOCOMPLETE_SEARCH_SET_ERRORED = "USER_AUTOCOMPLETE_SEARCH_SET_ERRORED",
  USER_AUTOCOMPLETE_SEARCH_SET_LOADING = "USER_AUTOCOMPLETE_SEARCH_SET_LOADING"
}

const setError = (status: number, description: string) => {
  return {
    type: UserAutocompleteSearchActionType.USER_AUTOCOMPLETE_SEARCH_SET_ERRORED,
    payload: {
      errorCode: status,
      errorDescription: description
    }
  };
};

const searchLoaded = (loadedItems: UserAutocompleteItem[]) => {
  return {
    type: UserAutocompleteSearchActionType.USER_AUTOCOMPLETE_SEARCH_LOADED,
    payload: {
      loadedItems
    }
  };
};

export const setLoading = (): ISetUserAutocompleteSearchLoadingAction => ({
  type: UserAutocompleteSearchActionType.USER_AUTOCOMPLETE_SEARCH_SET_LOADING,
  payload: {}
});

export const unloadSearch = (): IUserAutocompleteSearchUnloadAction => ({
  type: UserAutocompleteSearchActionType.USER_AUTOCOMPLETE_SEARCH_UNLOAD,
  payload: {}
});

function assembleAutocompleteUrl(args: IGetUserAutocompleteResultsArguments): string {
  let url: string = endpoints.search.autocomplete();

  const queryArgs = {
    query: args.searchQuery,
    maxSuggestionsToGet: args.maxNumToGet
  };

  const stringified = queryString.stringify(queryArgs);

  url += `?${stringified}`;

  return url;
}

// runs the autocomplete search on the server asynchronously and dispatches a USER_AUTOCOMPLETE_SEARCH_LOADED action
export const getAutocompleteResult = (
  args: IGetUserAutocompleteResultsArguments
): ThunkAction<
  void,
  IAppState,
  {},
  IUserAutocompleteSearchLoadedAction | ISetUserAutocompleteSearchErroredAction
> => dispatch => {
  ApiRequest.fromPath(assembleAutocompleteUrl(args))
    .withAuthHeader()
    .get()
    .then((response: Response) => {
      ResponseHandler.handle(
        response,
        (result: ResponseResult) => {
          dispatch(searchLoaded(result.payload));
        },
        (result: ResponseResult) => {
          dispatch(setError(response.status, result.error.message));
        },
        () => {
          dispatch(setError(response.status, "An unknown error has occured"));
        }
      );
    });
};
